// OSM and FSA text is user-contributed and untrusted. React already escapes
// text content, so these helpers cover what React doesn't: link targets
// (javascript:/data: URLs) and invisible characters that spoof or break layout.

// C0 controls (except tab/newline/CR), DEL, zero-width and bidi overrides.
const CONTROL_CHARS =
  // eslint-disable-next-line no-control-regex -- stripping control characters is the point
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u200B-\u200F\u202A-\u202E\u2066-\u2069]/g;

/** Strip control/bidi-override characters, collapse whitespace, cap length. */
export function cleanText(input: unknown, maxLength = 200): string {
  if (typeof input !== 'string') return '';
  const cleaned = input.replace(CONTROL_CHARS, '').replace(/\s+/g, ' ').trim();
  return cleaned.length > maxLength ? `${cleaned.slice(0, maxLength - 1)}…` : cleaned;
}

/**
 * Return a safe absolute http(s) URL, or null. Bare domains ("pub.co.uk")
 * are common in OSM `website` tags, so they get https:// prepended.
 */
export function safeUrl(input: unknown): string | null {
  const text = cleanText(input, 500);
  if (!text || /\s/.test(text)) return null;
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(text) ? text : `https://${text}`;
  let url: URL;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  if (url.username || url.password || !url.hostname.includes('.')) return null;
  return url.href;
}

/** Return a tel: href for a plausible phone number, or null. */
export function safeTel(input: unknown): string | null {
  // OSM sometimes holds several numbers separated by ";", so use the first.
  const first = cleanText(input, 60).split(';')[0]?.trim() ?? '';
  const digits = first.replace(/[^\d+]/g, '');
  if (!/^\+?\d{7,15}$/.test(digits)) return null;
  return `tel:${digits}`;
}
